
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Info } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

interface NutritionSummaryProps {
  dayName: string;
  selectedDate: Date;
  dayTotals: {
    calories: number;
    protein: number;
    carbs: number;
    fat: number;
    iron: number;
    calcium: number;
    zinc: number;
    magnesium: number;
    omega3: number;
  } | null;
  targets: {
    calories: number;
    protein: number;
    carbs: number;
    fat: number;
    iron: number;
    calcium: number;
    zinc: number;
    magnesium: number;
    omega3: number;
  };
}

const NutritionSummary: React.FC<NutritionSummaryProps> = ({
  dayName,
  selectedDate,
  dayTotals,
  targets
}) => {
  if (!dayTotals) return null;

  console.log('🎯 NutritionSummary DEBUG:', {
    targets,
    dayTotals,
    proteinTarget: targets.protein,
    proteinConsumed: dayTotals.protein
  });

  const caloriesProgress = Math.min(Math.round((dayTotals.calories / targets.calories) * 100), 100);
  const proteinProgress = Math.min(Math.round((dayTotals.protein / targets.protein) * 100), 100);
  const carbsProgress = Math.min(Math.round((dayTotals.carbs / targets.carbs) * 100), 100);
  const fatProgress = Math.min(Math.round((dayTotals.fat / targets.fat) * 100), 100);
  const ironProgress = Math.min(Math.round((dayTotals.iron / targets.iron) * 100), 100);
  const calciumProgress = Math.min(Math.round((dayTotals.calcium / targets.calcium) * 100), 100);
  const zincProgress = Math.min(Math.round((dayTotals.zinc / targets.zinc) * 100), 100);
  const magnesiumProgress = Math.min(Math.round((dayTotals.magnesium / targets.magnesium) * 100), 100);
  const omega3Progress = Math.min(Math.round((dayTotals.omega3 / targets.omega3) * 100), 100);

  return (
    <Card className="mb-6 bg-white shadow-sm border-0">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg flex items-center gap-2">
          <Info size={18} />
          Resumen Nutricional - {dayName} {format(selectedDate, "d MMM", { locale: es })}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span>Calorías</span>
              <span className="font-medium">{Math.round(dayTotals.calories)} / {targets.calories} kcal</span>
            </div>
            <Progress value={caloriesProgress} className="h-2" />
          </div>
          
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span>Proteínas</span>
              <span className="font-medium">{Math.round(dayTotals.protein)} / {targets.protein} g</span>
            </div>
            <Progress value={proteinProgress} className="h-2 bg-blue-100" indicatorClassName="bg-blue-500" />
          </div>
          
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span>Carbohidratos</span>
              <span className="font-medium">{Math.round(dayTotals.carbs)} / {targets.carbs} g</span>
            </div>
            <Progress value={carbsProgress} className="h-2 bg-amber-100" indicatorClassName="bg-amber-500" />
          </div>
          
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span>Grasas</span>
              <span className="font-medium">{Math.round(dayTotals.fat)} / {targets.fat} g</span>
            </div>
            <Progress value={fatProgress} className="h-2 bg-green-100" indicatorClassName="bg-green-500" />
          </div>
          
          <div className="mt-6 pt-4 border-t border-gray-100">
            <h3 className="text-sm font-medium mb-3">Micronutrientes</h3>
            
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Hierro</span>
                <span className="font-medium">{Math.round(dayTotals.iron)} / {targets.iron} mg</span>
              </div>
              <Progress value={ironProgress} className="h-2 bg-red-100" indicatorClassName="bg-red-500" />
            </div>
            
            <div className="space-y-2 mt-2">
              <div className="flex justify-between text-sm">
                <span>Calcio</span>
                <span className="font-medium">{Math.round(dayTotals.calcium)} / {targets.calcium} mg</span>
              </div>
              <Progress value={calciumProgress} className="h-2 bg-gray-100" indicatorClassName="bg-gray-500" />
            </div>
            
            <div className="space-y-2 mt-2">
              <div className="flex justify-between text-sm">
                <span>Zinc</span>
                <span className="font-medium">{Math.round(dayTotals.zinc)} / {targets.zinc} mg</span>
              </div>
              <Progress value={zincProgress} className="h-2 bg-purple-100" indicatorClassName="bg-purple-500" />
            </div>
            
            <div className="space-y-2 mt-2">
              <div className="flex justify-between text-sm">
                <span>Magnesio</span>
                <span className="font-medium">{Math.round(dayTotals.magnesium)} / {targets.magnesium} mg</span>
              </div>
              <Progress value={magnesiumProgress} className="h-2 bg-teal-100" indicatorClassName="bg-teal-500" />
            </div>
            
            <div className="space-y-2 mt-2">
              <div className="flex justify-between text-sm">
                <span>Omega 3</span>
                <span className="font-medium">{Math.round(dayTotals.omega3)} / {targets.omega3} g</span>
              </div>
              <Progress value={omega3Progress} className="h-2 bg-blue-100" indicatorClassName="bg-blue-600" />
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default NutritionSummary;
